import { memo } from 'react';
import { CATALOGO } from '../../catalogo/catalogo';
import type { Punto } from '../../nucleo/dominio/espacio';
import type { PlantaResumida } from '../../persistencia/planta';

const ANCHO = 160;
const ALTO = 100;

/** Relleno por rol operativo, con los tonos de la miniatura del Taller base (paleta A). */
const RELLENO: Record<string, string> = {
  bahia: '#9C9488',
  recepcion: '#8796AB',
  patio: '#CFC7BB',
  lavado: '#86A3AE',
  entrega: '#B8AFA2',
};

/**
 * Miniatura de planta en SVG de un proyecto (paso 1.5, como en el v6): marcos, pisos, elementos
 * coloreados por su rol, vías y muros. Barata: sin 3D ni texturas.
 */
export const MiniaturaProyecto = memo(function MiniaturaProyecto({
  planta,
}: {
  planta: PlantaResumida;
}) {
  const todos = planta.marcos.flat();
  if (todos.length === 0)
    return <svg className="miniatura" viewBox={`0 0 ${ANCHO} ${ALTO}`} aria-hidden="true" />;
  const xs = todos.map((p) => p.x);
  const zs = todos.map((p) => p.z);
  const [x0, x1, z0, z1] = [
    Math.min(...xs) - 2,
    Math.max(...xs) + 2,
    Math.min(...zs) - 2,
    Math.max(...zs) + 2,
  ];
  const k = Math.min(ANCHO / (x1 - x0), ALTO / (z1 - z0));
  const ox = (ANCHO - (x1 - x0) * k) / 2;
  const oz = (ALTO - (z1 - z0) * k) / 2;
  const t = (p: Punto) => `${((p.x - x0) * k + ox).toFixed(1)},${((p.z - z0) * k + oz).toFixed(1)}`;
  const puntos = (ps: Punto[]) => ps.map(t).join(' ');
  return (
    <svg className="miniatura" viewBox={`0 0 ${ANCHO} ${ALTO}`} aria-hidden="true">
      <rect width={ANCHO} height={ALTO} rx="4" fill="#E3DDD3" />
      {planta.marcos.map((m, i) => (
        <polygon
          key={`m${i}`}
          points={puntos(m)}
          fill="#F3EFE9"
          stroke="#C9C1B5"
          strokeWidth="0.8"
        />
      ))}
      {planta.pisos.map((p, i) => (
        <polygon key={`p${i}`} points={puntos(p)} fill="#E9E4DC" />
      ))}
      {planta.vias
        .filter((v) => !v.peatonal)
        .map((v, i) => (
          <polyline
            key={`v${i}`}
            points={puntos(v.puntos)}
            fill="none"
            stroke="#CFC7BB"
            strokeWidth={Math.max(1.2, 3.5 * k)}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      {planta.elementos.map((e, i) => {
        const rol = CATALOGO[e.catalogoId]?.rolOperativo?.estacion;
        if (rol === 'ingreso' || rol === 'salida') return null;
        return (
          <polygon
            key={`e${i}`}
            points={puntos(e.puntos)}
            fill={(rol && RELLENO[rol]) ?? '#FBF9F6'}
            stroke="#8E877C"
            strokeWidth="0.4"
          />
        );
      })}
      {planta.muros.map((m, i) => (
        <polyline key={`w${i}`} points={puntos(m)} fill="none" stroke="#5E6168" strokeWidth="1" />
      ))}
    </svg>
  );
});
